/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  FormControlLabel,
  FormHelperText,
  OutlinedInput,
  Select,
  Switch,
} from '@mui/material';
import InputLabel from '@mui/material/InputLabel';
import { useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import { Button } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import OutlinedTextField from 'src/components/textboxes/OutlinedTextField';
import { updateAuthenticationAdmin } from 'src/creators/Organisation.creators';
import * as Types from '../../../../types';
import { ButtonRow, Dropdown, SCIMContainer } from './SCIMSetup.styles';

type Props = {
  provider: string;
  scimProvider: Types.AuthenticationAdmin['scimProvider'];
  organisation: Types.State['organisation'];
  updateAuthenticationAdmin: (adminSettings: any) => Promise<any>;
};

const secretLocations = ['none', 'header', 'bearer', 'query'];

const SCIMSetup = (props: Props) => {
  const [state, setState] = useState({
    provider: props.provider ?? '',
    active: props.scimProvider?.active ?? false,
    secret: undefined,
    secretLocation: props.scimProvider?.secretLocation ?? secretLocations[0],
    secretLocationKey: props.scimProvider?.secretLocationKey ?? '',
  });

  const [saveDisabled, setSaveDisabled] = useState(false);

  const [methodError, setError] = useState(false);

  const submitSCIMSetup = async (event: any) => {
    event.preventDefault();
    const { organisation } = props;
    const scimProvider = state;

    const data = {
      scimProvider,
      organisation: {
        authenticationType: organisation.organisation?.authenticationType,
        slug: organisation.organisation?.slug,
      },
    };
    try {
      await props.updateAuthenticationAdmin(data);
      setSaveDisabled(true);
      handleSuccessToast({
        toastMessage: 'SCIM Provider updated successfully',
      });
    } catch (error: any) {
      const {
        response: {
          data: { errors },
        },
      } = error;
      handleErrorToast({ toastMessage: errors[0]?.message });
    }
  };

  const updateState = (data: any) => {
    setSaveDisabled(false);
    setState(data);
  };

  return (
    <SCIMContainer onSubmit={submitSCIMSetup}>
      <Dropdown>
        <InputLabel htmlFor={'method'}>
          {'Method of providing SCIM secret location'}
        </InputLabel>

        <Select
          native
          value={state.secretLocation}
          onChange={(event: any) => {
            updateState({ ...state, secretLocation: event.target.value });
            setError(false);
          }}
          inputProps={{
            name: 'method',
            id: 'method',
          }}
          input={
            <OutlinedInput
              className="authentication-admin-select-input"
              notched={false}
              inputProps={{
                style: {
                  padding: 12,
                  fontSize: 14,
                },
              }}
            />
          }
        >
          {secretLocations.map((location) => {
            if (location === 'none')
              return (
                <option value={location} key={'Select'}>
                  Select Method
                </option>
              );
            return (
              <option value={location} key={location}>
                {location}
              </option>
            );
          })}
        </Select>
      </Dropdown>
      {methodError && <FormHelperText>This is required!</FormHelperText>}

      {state.secretLocation !== 'bearer' && (
        <OutlinedTextField
          id={'secret-location-key'}
          placeholder="What your secret location key will be named eg. token, secret"
          label="Secret Location Key:"
          value={state.secretLocationKey}
          onChange={({ target: { value } }) => {
            updateState({ ...state, secretLocationKey: value });
          }}
        ></OutlinedTextField>
      )}

      <OutlinedTextField
        id={'secret'}
        type="password"
        placeholder="Your SCIM provider secret token, this will be stored as a hash"
        label="Secret:"
        value={
          props.scimProvider && !state.secret
            ? 'totallysecretpassword'
            : state.secret
        }
        onChange={({ target: { value } }) => {
          updateState({ ...state, secret: value });
        }}
      ></OutlinedTextField>

      <FormControlLabel
        label={'Set as active SCIM Provider'}
        control={
          <Switch
            id="active"
            color="primary"
            checked={state.active}
            onChange={() => {
              updateState({ ...state, active: !state.active });
            }}
          />
        }
      ></FormControlLabel>

      <ButtonRow>
        <Button
          variant="primary"
          size="medium"
          type="submit"
          disabled={saveDisabled}
          width="120px"
        >
          <>
            <StyledIconInButton
              size="medium"
              position="left"
              className="uil uil-save"
            />
            Save
          </>
        </Button>
      </ButtonRow>
    </SCIMContainer>
  );
};

const mapStateToProps = ({ organisation }: Types.State, ownProps: any) => ({
  scimProvider: organisation.authenticationAdmin?.scimProvider,
  organisation,
  ...ownProps,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators({ updateAuthenticationAdmin }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(SCIMSetup);
